import { useEffect, useMemo, useState } from 'react';
import type { CatalogProvider, Category, ModelSummary, ProductModel } from '@ox/schema';
import { useEditorStore } from '../store/editor-store';
import { swatchStyle } from '../lib/swatchStyle';
import {
  modelShowsColors,
  modelShowsSizes,
  modelSizeAxisLabel,
  pluralizeAxisLabel,
} from '../lib/modelAxes';

function summaryFromModel(m: ProductModel): ModelSummary {
  return {
    id: m.id,
    name: m.name,
    thumbnail:
      m.colors.find((c) => c.key === m.colors[0]?.key)?.mockups.front?.url ??
      m.colors[0]?.mockups.front?.url ??
      '',
    colorCount: m.colors.length,
    priceFrom: m.priceFrom,
    categoryId: m.category?.id,
  };
}

function CategoryTreeButtons({
  categories,
  categoryId,
  onSelect,
  depth = 0,
}: {
  categories: Category[];
  categoryId: string;
  onSelect: (id: string) => void;
  depth?: number;
}) {
  return (
    <>
      {categories.map((c) => {
        const kids = c.children ?? [];
        return (
          <div key={c.id} className="ox-cat-node">
            <button
              type="button"
              className={categoryId === c.id ? 'active' : ''}
              style={{ paddingLeft: 10 + depth * 14 }}
              onClick={() => onSelect(c.id)}
            >
              {c.label}
            </button>
            {kids.length > 0 && (
              <CategoryTreeButtons
                categories={kids}
                categoryId={categoryId}
                onSelect={onSelect}
                depth={depth + 1}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

export function ChangeProductModal({
  catalog,
  onClose,
}: {
  catalog: CatalogProvider;
  onClose: () => void;
}) {
  const changeModel = useEditorStore((s) => s.changeModel);
  const storeModel = useEditorStore((s) => s.model);
  const storeColorKey = useEditorStore((s) => s.document.selection.colorKey);
  const [categories, setCategories] = useState<Category[]>([]);
  // Always open on "all" so the full catalog is visible; current product is on the right.
  const [categoryId, setCategoryId] = useState('populares');
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [selected, setSelected] = useState<ProductModel | null>(() => storeModel);
  const [colorKey, setColorKey] = useState(storeColorKey);

  useEffect(() => {
    void catalog.listCategories().then((list) => {
      setCategories(Array.isArray(list) ? list : []);
    });
  }, [catalog]);

  useEffect(() => {
    void catalog.listModels({ categoryId }).then((list) => {
      setModels(Array.isArray(list) ? list : []);
    });
  }, [catalog, categoryId]);

  const gridModels = useMemo(() => {
    if (!storeModel) return models;
    if (models.some((m) => String(m.id) === String(storeModel.id))) return models;
    // Keep the product being edited visible in the grid even if it belongs to another category.
    return [summaryFromModel(storeModel), ...models];
  }, [models, storeModel]);

  // Detail pane: keep current design product + color until the user picks another card.
  useEffect(() => {
    if (!storeModel) return;
    setSelected(storeModel);
    setColorKey(storeColorKey);
  }, [storeModel, storeColorKey]);

  return (
    <div className="ox-modal-backdrop" onClick={onClose}>
      <div className="ox-modal ox-modal-wide" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>Cambiar producto</h2>
          <button type="button" onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="ox-change-layout">
          <nav className="ox-cat-nav">
            <CategoryTreeButtons
              categories={categories}
              categoryId={categoryId}
              onSelect={setCategoryId}
            />
          </nav>
          <div className="ox-product-grid">
            {gridModels.map((m) => (
              <button
                key={m.id}
                type="button"
                className={String(selected?.id) === String(m.id) ? 'active' : ''}
                onClick={() =>
                  void catalog.getModel(m.id).then((full) => {
                    setSelected(full);
                    const keep =
                      String(full.id) === String(storeModel?.id) &&
                      full.colors.some((c) => c.key === storeColorKey);
                    setColorKey(
                      keep ? storeColorKey : (full.colors[0]?.key ?? 'negro'),
                    );
                  })
                }
              >
                <img src={m.thumbnail} alt="" />
                <strong>{m.name}</strong>
                <span>
                  {m.colorCount > 1
                    ? `${m.colorCount} opciones`
                    : m.priceFrom
                      ? `${m.priceFrom.currency} ${m.priceFrom.amount.toFixed(2)}`
                      : ''}
                </span>
              </button>
            ))}
            {!gridModels.length && (
              <p className="ox-hint">No hay productos en esta categoría.</p>
            )}
          </div>
          {selected && (
            <aside className="ox-product-detail">
              <img
                src={
                  selected.colors.find((c) => c.key === colorKey)?.mockups.front?.url ??
                  selected.colors[0]?.mockups.front?.url
                }
                alt=""
              />
              <h3>{selected.name}</h3>
              {selected.rating && (
                <p>
                  ★ {selected.rating.value} ({selected.rating.count})
                </p>
              )}
              <p className="ox-price">
                {selected.priceFrom.currency} {selected.priceFrom.amount.toFixed(2)}
              </p>
              {modelShowsColors(selected) && (
                <>
                  <p>Opciones disponibles</p>
                  <div className="ox-swatches">
                    {selected.colors.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        className={colorKey === c.key ? 'active' : ''}
                        style={swatchStyle(c.hex)}
                        title={c.label}
                        onClick={() => setColorKey(c.key)}
                      />
                    ))}
                  </div>
                </>
              )}
              {modelShowsSizes(selected) && (
                <p>
                  {pluralizeAxisLabel(modelSizeAxisLabel(selected))} disponibles:{' '}
                  {selected.sizeRangeLabel}
                </p>
              )}
              <button
                type="button"
                className="ox-primary"
                onClick={() => {
                  changeModel(selected, colorKey);
                  onClose();
                }}
              >
                Seleccionar producto
              </button>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
